import {
  useUpdateBankAccountName,
  useUpdateInvestmentAccountName,
  BankAccountType,
  type InvestmentAccountType,
} from '@egw/api'
import { Button, ButtonVariant, Input } from '@egw/components'
import { CUSTOMER_SERVICE_PHONE_NUMBER } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { useState, type FC } from 'react'
import { Controller, useForm } from 'react-hook-form'

export interface AccountNameFormProps {
  alias: number
  accountName?: string
  onSuccess: () => void | Promise<void>
  accountType: BankAccountType | InvestmentAccountType
}

interface FormData {
  name: string
}

export const AccountNameForm: FC<AccountNameFormProps> = ({ alias, accountName, onSuccess, accountType }) => {
  const [formError, setFormError] = useState(false)

  const { t } = useTranslation('features-account')

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors, isDirty, isValid },
  } = useForm({
    defaultValues: {
      name: accountName,
    },
  })

  const { mutate: updateBankAccount, isLoading: isUpdatingBankAccountName } = useUpdateBankAccountName({
    onSuccess,
    onError: () => {
      setFormError(true)
    },
  })
  const { mutate: updateInvestmentAccount, isLoading: isUpdatingInvestmentAccountName } =
    useUpdateInvestmentAccountName({
      onSuccess,
      onError: () => {
        setFormError(true)
      },
    })

  const onSubmit = handleSubmit((formData: unknown): void => {
    setFormError(false)
    const data = formData as FormData
    if (accountType === BankAccountType.HIGH_YIELD_CHECKING || accountType === BankAccountType.HIGH_YIELD_SAVINGS) {
      void updateBankAccount({ alias, accountName: data.name })
    } else {
      void updateInvestmentAccount({ alias, accountName: data.name })
    }
  })

  return (
    <>
      <p className="text-base text-body mb-4">{t('account_details.enter_account_name')}</p>

      {formError ? (
        <div className="flex bg-alertErrorBg p-3 mb-4 rounded-md">
          <InformationCircleIcon className="w-6 h-6 fill-red-500" />
          <p className="text-sm text-alertErrorText ml-2">
            {t('account_details.update_error')}{' '}
            <a
              className="text-btnLinkText hover:text-btnLinkTextHover hover:underline font-semibold"
              href={`tel:${CUSTOMER_SERVICE_PHONE_NUMBER}`}
            >
              {t('account_details.contact')}
            </a>
          </p>
        </div>
      ) : null}

      <form>
        <div className="mb-4">
          <Controller
            name="name"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                {...field}
                label={t('account_details.account_name')}
                id="input-account-name"
                testId="input-account-name"
                required
                error={formErrors.name?.message}
                maxLength={35}
              />
            )}
          />
        </div>

        <Button
          variant={ButtonVariant.Primary}
          className="w-full"
          onClick={(e) => void onSubmit(e)}
          isDisabled={isUpdatingBankAccountName || isUpdatingInvestmentAccountName || !isDirty || !isValid}
        >
          {t('account_details.update')}
        </Button>
      </form>
    </>
  )
}
